import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { usePortalDetail } from "@/routes/portals.$id";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
	Check,
	Clock,
	Copy,
	ExternalLink,
	Globe,
	Monitor,
	Pencil,
	Rocket,
	Smartphone,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

export const Route = createFileRoute("/portals/$id/overview")({
	component: PortalOverview,
});

const API_BASE = import.meta.env.VITE_API_URL ?? "http://localhost:50051";

function getPreviewUrl(sessionId: string): string {
	try {
		const url = new URL(API_BASE);
		const port = Number.parseInt(url.port || "50051", 10) + 60;
		return `${url.protocol}//${url.hostname}:${port}/preview/${sessionId}/`;
	} catch {
		return `http://localhost:50111/preview/${sessionId}/`;
	}
}

function formatTs(ts?: { seconds: bigint }): string {
	if (!ts) return "—";
	return new Date(Number(ts.seconds) * 1000).toLocaleString(undefined, {
		day: "numeric",
		month: "short",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	});
}

function PortalOverview() {
	const { t } = useTranslation("portals");
	const { portal } = usePortalDetail();
	const navigate = useNavigate();
	const [viewport, setViewport] = useState<"desktop" | "mobile">("desktop");
	const [copied, setCopied] = useState(false);

	if (!portal) return null;

	const hasPreview = !!portal.activeSessionId;
	const previewUrl = hasPreview
		? getPreviewUrl(portal.activeSessionId)
		: null;

	const copyUrl = () => {
		if (!previewUrl) return;
		navigator.clipboard.writeText(previewUrl);
		setCopied(true);
		toast.success(t("url_copied"));
		setTimeout(() => setCopied(false), 2000);
	}

	return (
		<div className="space-y-6">
			{/* Header */}
			<div className="flex items-center justify-between">
				<h2 className="text-lg font-semibold">{t("overview")}</h2>
				<div className="flex gap-2">
					{portal.storeUrl && (
						<Button
							variant="outline"
							size="sm"
							className="gap-1.5"
							onClick={() => window.open(portal.storeUrl, "_blank")}
						>
							<ExternalLink className="h-3.5 w-3.5" />
							{t("open_site")}
						</Button>
					)}
					<Button
						size="sm"
						className="gap-1.5"
						onClick={() =>
							navigate({
								to: "/portals/$id/build",
								params: { id: portal.id },
							})
						}
					>
						<Pencil className="h-3.5 w-3.5" />
						{t("edit")}
					</Button>
				</div>
			</div>

			{/* Info strip */}
			<div className="flex items-center gap-6 text-sm text-muted-foreground">
				{portal.publishedAt && (
					<div className="flex items-center gap-1.5">
						<Rocket className="h-3.5 w-3.5" />
						{t("published_at")} {formatTs(portal.publishedAt)}
					</div>
				)}
				{portal.updatedAt && (
					<div className="flex items-center gap-1.5">
						<Clock className="h-3.5 w-3.5" />
						{t("updated_at")} {formatTs(portal.updatedAt)}
					</div>
				)}
			</div>

			{/* Preview */}
			{previewUrl ? (
				<div className="rounded-xl border overflow-hidden bg-card">
					{/* Browser chrome */}
					<div className="flex items-center gap-2 px-4 py-2.5 border-b bg-muted/50">
						<div className="flex gap-1.5">
							<div className="w-3 h-3 rounded-full bg-red-400/80" />
							<div className="w-3 h-3 rounded-full bg-amber-400/80" />
							<div className="w-3 h-3 rounded-full bg-emerald-400/80" />
						</div>
						<div className="flex-1 flex items-center gap-2 mx-4 px-3 py-1 rounded-md bg-background border text-xs font-mono text-muted-foreground truncate">
							{previewUrl}
						</div>
						<div className="flex items-center gap-1">
							<Button
								variant="ghost"
								size="icon"
								className={cn("h-7 w-7", viewport === "desktop" && "bg-accent")}
								onClick={() => setViewport("desktop")}
								aria-label={t("desktop_view")}
								aria-pressed={viewport === "desktop"}
							>
								<Monitor className="h-3.5 w-3.5" />
							</Button>
							<Button
								variant="ghost"
								size="icon"
								className={cn("h-7 w-7", viewport === "mobile" && "bg-accent")}
								onClick={() => setViewport("mobile")}
								aria-label={t("mobile_view")}
								aria-pressed={viewport === "mobile"}
							>
								<Smartphone className="h-3.5 w-3.5" />
							</Button>
							<Button
								variant="ghost"
								size="icon"
								className="h-7 w-7"
								onClick={copyUrl}
								aria-label={t("copy")}
							>
								{copied ? (
									<Check className="h-3.5 w-3.5 text-emerald-500" />
								) : (
									<Copy className="h-3.5 w-3.5" />
								)}
							</Button>
							<Button
								variant="ghost"
								size="icon"
								className="h-7 w-7"
								onClick={() => window.open(previewUrl, "_blank")}
								aria-label={t("open_site")}
							>
								<ExternalLink className="h-3.5 w-3.5" />
							</Button>
						</div>
					</div>
					{/* Iframe */}
					<div
						className={cn(
							"mx-auto transition-all duration-300",
							viewport === "mobile" ? "max-w-[375px]" : "w-full",
						)}
					>
						<iframe
							src={previewUrl}
							title={portal.name}
							className="w-full border-0"
							style={{ height: "560px" }}
							sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
						/>
					</div>
				</div>
			) : (
				<div className="rounded-xl border flex flex-col items-center justify-center py-20 text-center bg-muted/5">
					<Globe className="h-10 w-10 text-muted-foreground/20 mb-3" />
					<p className="text-sm text-muted-foreground mb-3">
						{t("no_preview_session")}
					</p>
					<Button
						variant="outline"
						size="sm"
						className="gap-1.5"
						onClick={() =>
							navigate({
								to: "/portals/$id/build",
								params: { id: portal.id },
							})
						}
					>
						<Pencil className="h-3.5 w-3.5" />
						{t("start_build")}
					</Button>
				</div>
			)}

			{/* Quick info */}
			<div className="grid grid-cols-2 md:grid-cols-4 gap-4">
				<InfoCard label={t("address_label")} value={`/${portal.slug}`} />
				<InfoCard
					label={t("site_url")}
					value={portal.storeUrl || t("not_set")}
					muted={!portal.storeUrl}
				/>
				<InfoCard
					label={t("status_label")}
					value={
						{
							draft: t("status_draft"),
							building: t("status_building"),
							preview: t("status_preview"),
							published: t("status_published"),
							archived: t("status_archived"),
						}[portal.status] ?? portal.status
					}
				/>
				<InfoCard
					label={t("preview_label")}
					value={hasPreview ? t("available") : t("not_available")}
					muted={!hasPreview}
				/>
			</div>
		</div>
	)
}

function InfoCard({
	label,
	value,
	muted,
}: { label: string; value: string; muted?: boolean }) {
	return (
		<div className="rounded-lg border p-3">
			<p className="text-xs text-muted-foreground">{label}</p>
			<p
				className={cn(
					"text-sm font-medium truncate mt-0.5",
					muted && "text-muted-foreground",
				)}
			>
				{value}
			</p>
		</div>
	)
}
